import { CircularProgress } from '@mui/material';
import UPButton from 'components/button';
import { UPDropdown } from 'components/dropdown';
import ErrorBar from 'components/error-bar';
import GeneralConfirmModal from 'components/general-confirm-modal';
import UPInput from 'components/input';
import UPSidePanel, { UPSidePanelSection } from 'components/side-panel';
import { AbsenceReasonPolicy } from 'constants/AbsenceReasonPolicy';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ABSENCE_REASON__CONCURRENT_CHANGE,
  ABSENCE_REASON__DEACTIVATE,
  ABSENCE_REASON__DEACTIVATE_CONFIRM,
  ABSENCE_REASON__INVALID_REMUN_CODE,
  ABSENCE_REASON__NAME_EN,
  ABSENCE_REASON__NAME_FR,
  ABSENCE_REASON__NAME_NL,
  ABSENCE_REASON__NAME_REQUIRED,
  ABSENCE_REASON__NAME_TOO_LONG,
  ABSENCE_REASON__NEW_REASON,
  ABSENCE_REASON__OPTIONAL,
  ABSENCE_REASON__PANEL_TITLE,
  ABSENCE_REASON__PROOF_POLICY,
  ABSENCE_REASON__REMARK_POLICY,
  ABSENCE_REASON__REMUN_CODE,
  ABSENCE_REASON__REMUN_CODE_DESCRIPTION,
  ABSENCE_REASON__REMUN_CODE_DESCRIPTION_REQUIRED,
  ABSENCE_REASON__REMUN_CODE_DESCRIPTION_TOO_LONG,
  ABSENCE_REASON__REMUN_CODE_REQUIRED,
  ABSENCE_REASON__REQUIRED,
  GENERAL__CLOSE,
  GENERAL__RETRY,
  GENERAL__SAVE_BUTTON,
} from 'translations/constants';
import { getReasonName } from 'utils/absence-reason';
import { NAME_MAX_LENGTH, REMUN_CODE_DESCRIPTION_MAX_LENGTH } from './constants';
import { UseAbsenceReasonPanelResult } from './hooks/useAbsenceReasonPanel';
import { PanelErrorRow, PanelFieldRow, PanelFields, PanelLinkButton } from './styles';
import { AbsenceReasonFormValues, EMPTY_FORM_VALUES, isValidRemunCode, toFormValues } from './utils';

interface AbsenceReasonPanelProps {
  panel: UseAbsenceReasonPanelResult;
}

const NAME_FIELDS = [
  { name: 'nameNl', labelKey: ABSENCE_REASON__NAME_NL, testId: 'absence-reason-name-nl' },
  { name: 'nameFr', labelKey: ABSENCE_REASON__NAME_FR, testId: 'absence-reason-name-fr' },
  { name: 'nameEn', labelKey: ABSENCE_REASON__NAME_EN, testId: 'absence-reason-name-en' },
] as const;

const AbsenceReasonPanel = ({ panel }: AbsenceReasonPanelProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const { status, mode, reason, isSaving, loadErrorKey, saveErrorKey, serverFieldError } = panel;
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid },
  } = useForm<AbsenceReasonFormValues>({ defaultValues: EMPTY_FORM_VALUES, mode: 'onChange' });

  // Fill the form each time a (re)load completes: on open, on retry, and after a concurrent change.
  useEffect(() => {
    if (status === 'ready') reset(reason ? toFormValues(reason) : EMPTY_FORM_VALUES);
  }, [status, reason, reset]);

  useEffect(() => {
    if (serverFieldError) setError(serverFieldError.field, { type: 'server', message: t(serverFieldError.key) });
  }, [serverFieldError, setError, t]);

  const submit = handleSubmit(panel.save);
  const policyItems = [
    { value: AbsenceReasonPolicy.OPTIONAL, label: t(ABSENCE_REASON__OPTIONAL) },
    { value: AbsenceReasonPolicy.REQUIRED, label: t(ABSENCE_REASON__REQUIRED) },
  ];
  const reasonName = reason ? getReasonName(reason, i18n.language) : '';
  const subtitle = mode === 'add' ? t(ABSENCE_REASON__NEW_REASON) : reasonName;

  return (
    <>
      <UPSidePanel
        open={status !== 'closed'}
        title={t(ABSENCE_REASON__PANEL_TITLE)}
        subtitle={subtitle}
        onClose={panel.close}
        testId="absence-reason-panel"
        footer={
          <>
            <UPButton
              text={t(GENERAL__SAVE_BUTTON)}
              onClick={submit}
              disabled={status !== 'ready' || isSaving || !isValid}
              testId="absence-reason-save"
            />
            <UPButton text={t(GENERAL__CLOSE)} onClick={panel.close} outlined testId="absence-reason-close" />
          </>
        }
      >
        {status === 'loading' && <CircularProgress />}

        {status === 'loadError' && (
          <PanelErrorRow>
            <ErrorBar message={t(loadErrorKey)} />
            <UPButton text={t(GENERAL__RETRY)} onClick={panel.retryLoad} outlined testId="absence-reason-panel-retry" />
          </PanelErrorRow>
        )}

        {saveErrorKey && (
          <PanelErrorRow>
            <ErrorBar message={t(saveErrorKey)} />
            {saveErrorKey !== ABSENCE_REASON__CONCURRENT_CHANGE && (
              <UPButton
                text={t(GENERAL__RETRY)}
                onClick={panel.retryAction === 'deactivate' ? panel.confirmDeactivate : submit}
                disabled={isSaving || status !== 'ready'}
                outlined
                testId="absence-reason-save-retry"
              />
            )}
          </PanelErrorRow>
        )}

        {status === 'ready' && (
          <UPSidePanelSection title={subtitle} testId="absence-reason-details">
            <PanelFields data-testid="absence-reason-fields">
              {NAME_FIELDS.map(field => (
                <Controller
                  key={field.name}
                  name={field.name}
                  control={control}
                  rules={{
                    required: String(t(ABSENCE_REASON__NAME_REQUIRED)),
                    maxLength: { value: NAME_MAX_LENGTH, message: t(ABSENCE_REASON__NAME_TOO_LONG) },
                  }}
                  render={({ field: input }) => (
                    <UPInput
                      value={input.value}
                      onChange={input.onChange}
                      label={t(field.labelKey)}
                      placeholder={t(field.labelKey)}
                      required
                      error={!!errors[field.name]}
                      helperText={errors[field.name]?.message}
                      disabled={isSaving}
                      testId={field.testId}
                    />
                  )}
                />
              ))}

              <PanelFieldRow data-field-row>
                <Controller
                  name="remunCodeDescription"
                  control={control}
                  rules={{
                    required: String(t(ABSENCE_REASON__REMUN_CODE_DESCRIPTION_REQUIRED)),
                    validate: value =>
                      value.trim() !== '' || String(t(ABSENCE_REASON__REMUN_CODE_DESCRIPTION_REQUIRED)),
                    maxLength: {
                      value: REMUN_CODE_DESCRIPTION_MAX_LENGTH,
                      message: t(ABSENCE_REASON__REMUN_CODE_DESCRIPTION_TOO_LONG),
                    },
                  }}
                  render={({ field: input }) => (
                    <UPInput
                      value={input.value}
                      onChange={input.onChange}
                      label={t(ABSENCE_REASON__REMUN_CODE_DESCRIPTION)}
                      placeholder={t(ABSENCE_REASON__REMUN_CODE_DESCRIPTION)}
                      required
                      error={!!errors.remunCodeDescription}
                      helperText={errors.remunCodeDescription?.message}
                      disabled={isSaving}
                      testId="absence-reason-remun-code-description"
                    />
                  )}
                />
                <Controller
                  name="remunCode"
                  control={control}
                  rules={{
                    required: String(t(ABSENCE_REASON__REMUN_CODE_REQUIRED)),
                    validate: value => isValidRemunCode(value) || String(t(ABSENCE_REASON__INVALID_REMUN_CODE)),
                  }}
                  render={({ field: input }) => (
                    <UPInput
                      value={input.value}
                      onChange={input.onChange}
                      label={t(ABSENCE_REASON__REMUN_CODE)}
                      placeholder={t(ABSENCE_REASON__REMUN_CODE)}
                      required
                      error={!!errors.remunCode}
                      helperText={errors.remunCode?.message}
                      disabled={isSaving}
                      testId="absence-reason-remuncode"
                    />
                  )}
                />
              </PanelFieldRow>

              <PanelFieldRow data-field-row>
                {(
                  [
                    {
                      name: 'remarkPolicy',
                      labelKey: ABSENCE_REASON__REMARK_POLICY,
                      testId: 'absence-reason-remark-policy',
                    },
                    {
                      name: 'proofPolicy',
                      labelKey: ABSENCE_REASON__PROOF_POLICY,
                      testId: 'absence-reason-proof-policy',
                    },
                  ] as const
                ).map(field => (
                  <Controller
                    key={field.name}
                    name={field.name}
                    control={control}
                    render={({ field: input }) => (
                      <UPDropdown
                        value={input.value}
                        onChange={event => input.onChange(event.target.value)}
                        label={t(field.labelKey)}
                        items={policyItems}
                        disabled={isSaving}
                        testId={field.testId}
                      />
                    )}
                  />
                ))}
              </PanelFieldRow>

              {mode === 'edit' && reason?.isActive && (
                <PanelLinkButton
                  type="button"
                  onClick={panel.requestDeactivate}
                  disabled={isSaving}
                  data-testid="absence-reason-deactivate"
                >
                  {t(ABSENCE_REASON__DEACTIVATE)}
                </PanelLinkButton>
              )}
            </PanelFields>
          </UPSidePanelSection>
        )}
      </UPSidePanel>

      <GeneralConfirmModal
        open={panel.isConfirmingDeactivate}
        onClose={panel.cancelDeactivate}
        onAction={panel.confirmDeactivate}
        content={t(ABSENCE_REASON__DEACTIVATE_CONFIRM)}
        confirmTestId="absence-reason-deactivate-confirm"
        cancelTestId="absence-reason-deactivate-cancel"
        actionDisabled={isSaving}
      />
    </>
  );
};

export default AbsenceReasonPanel;
